import React, { useEffect, useRef, useState, useCallback, memo } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import { ChatWindow } from "./components/Chat/Chat";
import { useAppState } from "./state";
import MicIcon from "@material-ui/icons/Mic";
import MicOffIcon from "@material-ui/icons/MicOff";
import VideocamIcon from "@material-ui/icons/Videocam";
import VideocamOffIcon from "@material-ui/icons/VideocamOff";
import ScreenShareIcon from "@material-ui/icons/ScreenShare";
import CallEndIcon from "@material-ui/icons/CallEnd";
import PeopleIcon from "@material-ui/icons/People";
import "./App.css";
import "./video-grid.css";
import "./ZoomUI.css";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import { PreJoinScreens } from "./PreJoinScreens";
import { color } from "../../style/constants";
import VideoParticipant from "./VideoParticipant";
import ControlButton from "./ControlButton";
import {
  createVideoPlaceholder as createPlaceholder,
  attachVideoToContainer
} from "./utils";
import useZoomClient from "./useZoomClient";

interface UserType {
  userId: string;
  userIdentity: string;
  displayName: string;
  isHost: boolean;
  bVideoOn: boolean;
  muted: boolean;
}

interface VideoAppProps {
  coachingId: string;
}

export const VideoApp: React.FC<VideoAppProps> = memo(({ coachingId }) => {
  const { fetchZoomSignature, user } = useAppState();
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const username = user?.displayName || "Participant";
  const password = process.env.REACT_APP_ZOOM_PASSWORD;
  const [messages, setMessages] = useState<any[]>([]);
  const { client, isInitializing } = useZoomClient();
  const [remoteUsers, setRemoteUsers] = useState<UserType[]>([]);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [localAudioMuted, setLocalAudioMuted] = useState(true);
  const [sidebar, setSidebar] = useState<"participants" | "chat" | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [videoOn, setVideoOn] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [hasActiveShare, setHasActiveShare] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [preJoinAccepted, setPreJoinAccepted] = useState(false);
  const [recordingAllowed, setRecordingAllowed] = useState(false);

  // Function to create placeholder with user initials when video is off
  const createVideoPlaceholder = useCallback(() => {
    if (!videoContainerRef.current) return;
    createPlaceholder(videoContainerRef.current, username, localAudioMuted);
  }, [username, localAudioMuted]);

  const handlePreJoinAccept = (
    accepted: boolean,
    recordingConsent?: boolean,
    mediaSettings?: { video: boolean; audio: boolean }
  ) => {
    setPreJoinAccepted(accepted);
    setRecordingAllowed(recordingConsent || false);

    if (accepted) {
      handleJoinWithSettings(mediaSettings);
    }
  };

  const handleJoinWithSettings = useCallback(
    (mediaSettings?: { video: boolean; audio: boolean }) => {
      if (mediaSettings) {
        setVideoOn(mediaSettings.video);
        setLocalAudioMuted(!mediaSettings.audio);
      }

      setConnecting(true);
    },
    []
  );

  // Add useEffect to handle join when connecting state changes
  useEffect(() => {
    if (connecting && !connected) {
      handleJoin();
    }
  }, [connecting, connected]);

  const handleStartRecording = async () => {
    try {
      await client.getRecordingClient().startCloudRecording();
      setIsRecording(true);
    } catch (error) {
      console.error("Failed to start recording:", error);
    }
  };

  const handleStopRecording = async () => {
    try {
      await client.getRecordingClient().stopCloudRecording();
      setIsRecording(false);
    } catch (error) {
      console.error("Failed to stop recording:", error);
    }
  };

  function getVideoOptions() {
    return {
      virtualBackground: {
        imageUrl: process.env.REACT_APP_ZOOM_BG_IMAGE
      }
    };
  }

  async function startLocalVideo(mediaStream: any) {
    const devices = await navigator.mediaDevices.enumerateDevices();
    if (devices.some(device => device.kind === "videoinput")) {
      const videoOptions = getVideoOptions();
      if (videoOptions) {
        try {
          await mediaStream.startVideo(videoOptions);
        } catch (error) {
          console.error("Failed to start video with options:", error);
          await mediaStream.startVideo();
        }
      } else {
        await mediaStream.startVideo();
      }
    }
  }

  const handleJoin = useCallback(async () => {
    if (!client) return;
    setConnecting(true);
    try {
      const token = await fetchZoomSignature(coachingId, true);
      const generatedSessionName = `${
        process.env.REACT_APP_ZOOM_SESSION_NAME
      } - ${coachingId.split("-")[0].toUpperCase()}`; //Coaching Call - 688B9AD3
      await client.join(generatedSessionName, token, username, password);

      const mediaStream = client.getMediaStream();

      // Handle audio based on prejoin state
      if (!localAudioMuted) {
        await mediaStream.startAudio();
      } else {
        await mediaStream.startAudio();
        await mediaStream.muteAudio();
      }

      // Handle video based on prejoin state
      if (videoOn) {
        await startLocalVideo(mediaStream);
        const userId = client.getCurrentUserInfo().userId;
        let attempts = 0;
        const attachVideo = async () => {
          if (!videoContainerRef.current) {
            if (attempts < 5) {
              attempts++;
              setTimeout(attachVideo, 200);
            }
            return;
          }
          videoContainerRef.current.innerHTML = "";
          try {
            await attachVideoToContainer(
              mediaStream,
              userId,
              videoContainerRef.current
            );
          } catch (e) {
            if (attempts < 5) {
              attempts++;
              setTimeout(attachVideo, 200);
            } else {
              console.error("Failed to attach local video after retries:", e);
            }
          }
        };
        attachVideo();
      } else {
        createVideoPlaceholder();
      }

      setConnected(true);
      const userId = client.getCurrentUserInfo().userId;
      setLocalAudioMuted(mediaStream.isAudioMuted(userId));
    } catch (err) {
      console.error("Failed to join meeting:", err);
    } finally {
      setConnecting(false);
    }
  }, [
    client,
    fetchZoomSignature,
    coachingId,
    username,
    password,
    videoOn,
    localAudioMuted,
    createVideoPlaceholder
  ]);

  const handleLeave = useCallback(async () => {
    if (!client) return;
    const mediaStream = client.getMediaStream();
    for (const user of client.getAllUser()) {
      await mediaStream.detachVideo(user.userId);
    }
    await client.leave();
    setConnected(false);
    setRemoteUsers([]);
    setMessages([]);
    setVideoOn(false);
    if (videoContainerRef.current) videoContainerRef.current.innerHTML = "";
  }, [client]);

  const handleToggleVideo = useCallback(async () => {
    if (!client) return;
    const mediaStream = client.getMediaStream();
    const userId = client.getCurrentUserInfo().userId;

    try {
      if (mediaStream.isCapturingVideo()) {
        if (videoContainerRef.current) {
          const existingVideo = videoContainerRef.current.querySelector(
            "video"
          );

          const tempDiv = document.createElement("div");
          const placeholderDiv = document.createElement("div");
          placeholderDiv.id = "video-placeholder";
          placeholderDiv.style.width = "100%";
          placeholderDiv.style.height = "100%";
          placeholderDiv.style.backgroundColor = "#000000";
          placeholderDiv.style.display = "flex";
          placeholderDiv.style.alignItems = "center";
          placeholderDiv.style.justifyContent = "center";
          placeholderDiv.style.position = "relative";

          const initialsDiv = document.createElement("div");
          initialsDiv.style.width = "80px";
          initialsDiv.style.height = "80px";
          initialsDiv.style.borderRadius = "50%";
          initialsDiv.style.backgroundColor = "#037dba";
          initialsDiv.style.display = "flex";
          initialsDiv.style.alignItems = "center";
          initialsDiv.style.justifyContent = "center";
          initialsDiv.style.color = "white";
          initialsDiv.style.fontSize = "32px";
          initialsDiv.style.fontWeight = "600";
          initialsDiv.textContent = username.charAt(0).toUpperCase();

          placeholderDiv.appendChild(initialsDiv);

          // Add the placeholder to the temp div
          tempDiv.appendChild(placeholderDiv);

          // Only replace the video with the placeholder after stopping video
          await mediaStream.stopVideo();
          setVideoOn(false);

          // Now replace the content
          videoContainerRef.current.innerHTML = "";
          videoContainerRef.current.appendChild(placeholderDiv);

          // Clean up the existing video element if it exists
          if (existingVideo) {
            existingVideo.remove();
          }

          // Detach video from Zoom SDK
          const element = await mediaStream.detachVideo(userId);
          if (element) {
            Array.isArray(element)
              ? element.forEach(el => el.remove())
              : element?.remove();
          }
        } else {
          await mediaStream.stopVideo();
          setVideoOn(false);
          const element = await mediaStream.detachVideo(userId);
          Array.isArray(element)
            ? element.forEach(el => el.remove())
            : element?.remove();
          createVideoPlaceholder();
        }
      } else {
        // First create a loading state
        if (videoContainerRef.current) {
          // Show loading indicator
          const loadingDiv = document.createElement("div");
          loadingDiv.style.width = "100%";
          loadingDiv.style.height = "100%";
          loadingDiv.style.backgroundColor = "#000000";
          loadingDiv.style.display = "flex";
          loadingDiv.style.alignItems = "center";
          loadingDiv.style.justifyContent = "center";
          loadingDiv.style.color = "white";
          loadingDiv.textContent = "Starting video...";

          videoContainerRef.current.innerHTML = "";
          videoContainerRef.current.appendChild(loadingDiv);
        }

        // Start video in the background
        await startLocalVideo(mediaStream);
        setVideoOn(true);

        if (videoContainerRef.current) {
          videoContainerRef.current.innerHTML = "";
          const userVideo = await mediaStream.attachVideo(
            userId,
            window.WebVideoSDK.VideoQuality.Video_720P
          );
          if (userVideo instanceof HTMLElement) {
            userVideo.style.width = "100%";
            userVideo.style.height = "100%";
            userVideo.style.objectFit = "cover";
          }
          videoContainerRef.current.appendChild(userVideo);
        }
      }
    } catch (err) {
      console.error("Error toggling video:", err);
      // Ensure we show the placeholder if there's an error
      if (!videoOn && videoContainerRef.current) {
        createVideoPlaceholder();
      }
    }
  }, [client, username, createVideoPlaceholder, localAudioMuted]);

  const handleToggleAudio = useCallback(async () => {
    if (!client) return;
    const mediaStream = client.getMediaStream();
    const userId = client.getCurrentUserInfo().userId;
    if (!mediaStream.isAudioMuted(userId)) {
      await mediaStream.muteAudio();
      setLocalAudioMuted(true);
    } else {
      await mediaStream.unmuteAudio();
      setLocalAudioMuted(false);
    }
  }, [client]);

  const startScreenShare = async () => {
    try {
      const stream = client.getMediaStream();
      if (stream.isStartShareScreenWithVideoElement()) {
        await stream.startShareScreen(
          document.querySelector("#my-screen-share-content-video")
        );
      } else {
        await stream.startShareScreen(
          document.querySelector("#my-screen-share-content-canvas")
        );
      }
      setIsSharing(true);
    } catch (err) {
      console.error("Error starting screen share", err);
    }
  };

  const stopScreenShare = async () => {
    try {
      const stream = client.getMediaStream();
      await stream.stopShareScreen();
      setIsSharing(false);
    } catch (err) {
      console.error("Error stopping screen share", err);
    }
  };

  // Track if recording has been manually stopped
  const [recordingManuallyStoppedRef] = useState({ current: false });

  // Auto-start recording when connected and recording is allowed
  useEffect(() => {
    // Only auto-start if not manually stopped before
    if (
      connected &&
      recordingAllowed &&
      client &&
      !isRecording &&
      !recordingManuallyStoppedRef.current
    ) {
      console.log("Auto-starting recording...");
      setTimeout(() => {
        handleStartRecording();
      }, 1000); // Small delay to ensure client is fully initialized
    }
  }, [
    connected,
    recordingAllowed,
    client,
    isRecording,
    recordingManuallyStoppedRef
  ]);

  useEffect(() => {
    if (!client) return;
    const stream = client.getMediaStream();

    const onUserAdded = (users: UserType[]) => {
      setRemoteUsers(prev => {
        const localUserId = client.getCurrentUserInfo().userId;
        const existingIdentities = new Set(prev.map(u => u.userIdentity));
        const newUsers = users.filter(
          u =>
            u.userId !== localUserId && !existingIdentities.has(u.userIdentity)
        );
        if (users.length > 0) {
          try {
            const recordingClient = client.getRecordingClient();

            let status = recordingClient.getCloudRecordingStatus();
            // If not recording, check again after a delay
            if (status !== "Recording") {
              setTimeout(() => {
                status = recordingClient.getCloudRecordingStatus();
                setIsRecording(status === "Recording");
              }, 2000); // 2 second delay
            }
          } catch (error) {
            console.error("Failed to check recording status:", error);
          }
        }
        return [...prev, ...newUsers];
      });
    };

    const onUserRemoved = (users: UserType[]) => {
      setRemoteUsers(prev => {
        const filtered = prev.filter(
          u => !users.some(removed => removed.userIdentity === u.userIdentity)
        );
        // Check recording status when users leave
        if (connected && users.length > 0) {
          try {
            const recordingClient = client.getRecordingClient();

            // First check if any of the removed users was the one recording
            const wasRecorderRemoved = users.some(user => user.isHost);
            if (wasRecorderRemoved) {
              // If the host/recorder left, assume recording has stopped
              setIsRecording(false);
            } else {
              const status = recordingClient.getCloudRecordingStatus();
              setIsRecording(status === "Recording");
            }
          } catch (error) {
            console.error("Failed to check recording status:", error);
            // If we can't check the status and users left, assume recording stopped
            setIsRecording(false);
          }
        }

        return filtered;
      });
    };

    const onChatMessage = (payload: any) => {
      if (payload.message) {
        try {
          const chat = client.getChatClient();
          setTimeout(() => setMessages(chat.getHistory()), 500);
        } catch (error) {
          console.error("Failed to get chat history:", error);
        }
      }
    };

    client.on("user-added", onUserAdded);
    client.on("user-removed", onUserRemoved);
    client.on("chat-on-message", onChatMessage);
    client.on("active-share-change", (payload: any) => {
      if (payload.state === "Active") {
        setHasActiveShare(true);
        setTimeout(() => {
          const canvas = document.getElementById(
            "users-screen-share-content-canvas"
          );
          if (canvas) {
            stream.startShareView(canvas, payload.userId);
          }
        }, 500);
      } else {
        stream.stopShareView();
        setHasActiveShare(false);
        // Give time for the UI to update before re-rendering videos
        setTimeout(() => {
          const renderVideos = async () => {
            // Re-render remote videos after screen share ends
            for (const user of remoteUsers) {
              if (!user.bVideoOn) continue;
              const container = document.getElementById(
                `remote-video-${user.userId}`
              );
              if (container) {
                try {
                  await stream.detachVideo(user.userId);
                  container.innerHTML = "";
                  const videoElement = await stream.attachVideo(
                    user.userId,
                    window.WebVideoSDK.VideoQuality.Video_720P
                  );
                  if (videoElement instanceof HTMLElement) {
                    videoElement.style.width = "100%";
                    videoElement.style.height = "100%";
                    videoElement.style.objectFit = "cover";
                    container.appendChild(videoElement);
                  }
                } catch (err) {
                  console.error(
                    `Failed to re-render video for user ${user.userId}:`,
                    err
                  );
                }
              }
            }
          };
          renderVideos();
        }, 800);
      }
    });

    const renderRemoteVideos = async () => {
      if (!client) return;
      for (const user of remoteUsers) {
        const container = document.getElementById(
          `remote-video-${user.userId}`
        );
        if (container) {
          // Always detach first to prevent black screens from stale video elements
          try {
            await stream.detachVideo(user.userId);
            container.innerHTML = "";
          } catch (err) {
            console.log(`Detach video cleanup for user ${user.userId}:`, err);
          }

          // Only try to attach if video is on
          if (user.bVideoOn) {
            try {
              const videoElement = await stream.attachVideo(
                user.userId,
                window.WebVideoSDK.VideoQuality.Video_720P
              );
              if (videoElement instanceof HTMLElement) {
                videoElement.style.width = "100%";
                videoElement.style.height = "100%";
                videoElement.style.objectFit = "cover";
                container.appendChild(videoElement);
              }
            } catch (err) {
              console.error(
                `Failed to attach video for user ${user.userId}:`,
                err
              );
            }
          }
        }
      }
    };

    renderRemoteVideos();

    return () => {
      client.off("user-added", onUserAdded);
      client.off("user-removed", onUserRemoved);
      client.off("chat-on-message", onChatMessage);
    };
  }, [client, remoteUsers]);

  useEffect(() => {
    if (!client) return;
    const stream = client.getMediaStream();

    const onUserUpdated = (users: UserType[]) => {
      setRemoteUsers(prev => {
        const updatedUsers = prev.map(user => {
          const updatedUser = users.find(u => u.userId === user.userId);
          if (updatedUser) {
            // If video state changed, handle video element update
            if (updatedUser.bVideoOn !== user.bVideoOn) {
              setTimeout(() => {
                const container = document.getElementById(
                  `remote-video-${user.userId}`
                );
                if (container) {
                  // Clean up existing video
                  stream.detachVideo(user.userId).catch((err: any) => {
                    console.log(
                      `Error detaching video for user ${user.userId}:`,
                      err
                    );
                  });
                  container.innerHTML = "";

                  // Attach new video if turned on
                  if (updatedUser.bVideoOn) {
                    stream
                      .attachVideo(
                        user.userId,
                        window.WebVideoSDK.VideoQuality.Video_720P
                      )
                      .then((videoElement: any) => {
                        if (videoElement instanceof HTMLElement) {
                          videoElement.style.width = "100%";
                          videoElement.style.height = "100%";
                          videoElement.style.objectFit = "cover";
                          container.appendChild(videoElement);
                        }
                      })
                      .catch((err: any) => {
                        console.error(
                          `Failed to attach video for user ${user.userId}:`,
                          err
                        );
                      });
                  }
                }
              }, 100);
            }
            return { ...user, ...updatedUser };
          }
          return user;
        });
        return updatedUsers;
      });
    };

    client.on("user-updated", onUserUpdated);
    return () => {
      client.off("user-updated", onUserUpdated);
    };
  }, [client]);

  // Effect to handle video rendering when screen sharing state changes
  useEffect(() => {
    if (!client || !connected) return;
    const stream = client.getMediaStream();

    // Re-render all videos when screen sharing state changes
    const renderAllVideos = async () => {
      // Handle local video
      if (videoContainerRef.current) {
        const userId = client.getCurrentUserInfo().userId;
        try {
          await stream.detachVideo(userId);

          if (videoOn) {
            videoContainerRef.current.innerHTML = "";
            const userVideo = await stream.attachVideo(
              userId,
              window.WebVideoSDK.VideoQuality.Video_720P
            );
            if (userVideo instanceof HTMLElement) {
              userVideo.style.width = "100%";
              userVideo.style.height = "100%";
              userVideo.style.objectFit = "cover";
              videoContainerRef.current.appendChild(userVideo);
            }
          } else {
            // Show placeholder when video is off
            createVideoPlaceholder();
          }
        } catch (err) {
          console.error("Failed to re-render local video:", err);
          // If video rendering fails, show placeholder
          if (!videoOn) {
            createVideoPlaceholder();
          }
        }
      }

      // Handle remote videos
      for (const user of remoteUsers) {
        if (!user.bVideoOn) continue;

        const container = document.getElementById(
          `remote-video-${user.userId}`
        );
        if (container) {
          try {
            await stream.detachVideo(user.userId);
            container.innerHTML = "";

            const videoElement = await stream.attachVideo(
              user.userId,
              window.WebVideoSDK.VideoQuality.Video_720P
            );
            if (videoElement instanceof HTMLElement) {
              videoElement.style.width = "100%";
              videoElement.style.height = "100%";
              videoElement.style.objectFit = "cover";
              container.appendChild(videoElement);
            }
          } catch (err) {
            console.error(
              `Failed to re-render video for user ${user.userId}:`,
              err
            );
          }
        }
      }
    };

    // Small delay to ensure DOM elements are ready
    setTimeout(renderAllVideos, 500);
  }, [
    client,
    connected,
    hasActiveShare,
    videoOn,
    remoteUsers,
    createVideoPlaceholder
  ]);

  // Effect to ensure video placeholder persists
  useEffect(() => {
    if (!connected || videoOn) return;

    // Check every second if the placeholder is still there
    const intervalId = setInterval(() => {
      if (
        videoContainerRef.current &&
        !videoContainerRef.current.querySelector("#video-placeholder")
      ) {
        createVideoPlaceholder();
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [connected, videoOn, createVideoPlaceholder]);

  // Update placeholder when audio mute state changes
  useEffect(() => {
    if (connected && !videoOn && videoContainerRef.current) {
      createVideoPlaceholder();
    }
  }, [localAudioMuted, connected, videoOn, createVideoPlaceholder]);

  // Update placeholder when audio mute state changes
  useEffect(() => {
    if (connected && !videoOn && videoContainerRef.current) {
      createVideoPlaceholder();
    }
  }, [localAudioMuted, connected, videoOn, createVideoPlaceholder]);

  useEffect(() => {
    if (!client) return;

    const onRecordingStatusChange = (payload: any) => {
      setIsRecording(payload.status === "Recording");
    };

    // Initial check when joining
    const checkRecordingStatus = async () => {
      if (connected) {
        try {
          const recordingClient = client.getRecordingClient();
          const status = await recordingClient.getCloudRecordingStatus();
          setIsRecording(status === "Recording");
        } catch (error) {
          console.error("Failed to check recording status:", error);
        }
      }
    };

    client.on("recording-status-change", onRecordingStatusChange);
    checkRecordingStatus();

    return () => {
      client.off("recording-status-change", onRecordingStatusChange);
    };
  }, [client, connected]);

  return (
    <>
      {!preJoinAccepted ? (
        <PreJoinScreens onJoin={handlePreJoinAccept} username={username} />
      ) : (
        <Box className="zoom-app-wrapper">
          <Card elevation={3} className="zoom-app-card">
            <CardContent className="zoom-app-content">
              {/* Screen sharing status indicator */}
              {hasActiveShare && (
                <Box className="zoom-screen-sharing-banner">
                  <ScreenShareIcon style={{ marginRight: 8 }} />
                  <Typography variant="body2">
                    Screen sharing in progress
                  </Typography>
                </Box>
              )}

              <Box className="zoom-layout">
                {/* Sidebar - Only show on desktop or when not in chat mode */}
                {(!isMobile || (isMobile && sidebar !== "chat")) && (
                  <Box
                    className="zoom-sidebar-modern"
                    style={{ width: isTablet ? "220px" : "280px" }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={2}
                    >
                      <Typography variant="h6" className="zoom-sidebar-title">
                        Video Call
                      </Typography>
                      <Box
                        bgcolor={color.PALEBLUE}
                        px={1.5}
                        py={0.5}
                        borderRadius={12}
                        display="flex"
                        alignItems="center"
                        style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}
                      >
                        <PeopleIcon
                          style={{
                            fontSize: 16,
                            marginRight: 4,
                            color: color.BLUE
                          }}
                        />
                        <Typography
                          variant="caption"
                          style={{ fontWeight: 600, color: color.DARKBLUE }}
                        >
                          {remoteUsers.length + 1}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Participants */}
                    <Box className="zoom-participants">
                      <Typography
                        variant="subtitle2"
                        className="zoom-section-title"
                      >
                        Participants
                      </Typography>
                      <Box className="zoom-participant-list">
                        <Box className="zoom-participant">
                          <Box
                            className="zoom-participant-avatar"
                            style={{
                              background: `linear-gradient(45deg, ${color.BLUE}, ${color.DARKBLUE})`,
                              boxShadow: "0 2px 4px rgba(0,0,0,0.15)"
                            }}
                          >
                            {username.charAt(0).toUpperCase()}
                          </Box>
                          <Typography
                            variant="body2"
                            style={{ fontWeight: 500 }}
                          >
                            {username} (You)
                          </Typography>
                          {localAudioMuted && (
                            <MicOffIcon className="zoom-muted-icon" />
                          )}
                        </Box>
                        {remoteUsers.map(user => (
                          <Box key={user.userId} className="zoom-participant">
                            <Box
                              className="zoom-participant-avatar"
                              style={{
                                background: `linear-gradient(45deg, ${color.PURPLE}, ${color.DARKPURPLE})`,
                                boxShadow: "0 2px 4px rgba(0,0,0,0.15)"
                              }}
                            >
                              {user.displayName.charAt(0).toUpperCase()}
                            </Box>
                            <Typography
                              variant="body2"
                              style={{ fontWeight: 500 }}
                            >
                              {user.displayName}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                      <Box style={{ height: "300px", marginTop: "16px" }}>
                        <ChatWindow messages={messages} zoomClient={client} />
                      </Box>
                    </Box>

                    {/* Video Thumbnails - Only show when screen sharing is active */}
                    {hasActiveShare && (
                      <Box className="zoom-video-thumbnails">
                        <Box className="zoom-video-thumbnail">
                          <video-player-container
                            ref={videoContainerRef}
                            className="zoom-video-container-modern"
                          ></video-player-container>
                          <Typography
                            variant="caption"
                            className="zoom-video-label"
                          >
                            You
                          </Typography>
                        </Box>
                        {remoteUsers.map(user => (
                          <Box
                            key={user.userId}
                            className="zoom-video-thumbnail"
                          >
                            <div
                              id={`remote-video-${user.userId}`}
                              className="zoom-video-container-modern"
                            />
                            <Typography
                              variant="caption"
                              className="zoom-video-label"
                            >
                              {user.displayName}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}

                {/* Main Content */}
                <Box className="zoom-main-area">
                  {hasActiveShare ? (
                    <Box className="zoom-screen-share">
                      <canvas
                        id="users-screen-share-content-canvas"
                        className="zoom-share-canvas"
                      />
                    </Box>
                  ) : !connected ? (
                    <Box
                      className="zoom-waiting-area"
                      style={{
                        background: `linear-gradient(135deg, ${color.PALEBLUE} 0%, ${color.LIGHTBLUE} 100%)`,
                        borderRadius: 8,
                        boxShadow: "inset 0 0 20px rgba(0,0,0,0.05)"
                      }}
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        p={4}
                      >
                        <Box className="zoom-waiting-icon" mb={3}>
                          <VideocamIcon
                            style={{ fontSize: 40, color: color.BLUE }}
                          />
                        </Box>
                        <Typography
                          variant="h5"
                          style={{ color: color.DARKBLUE, fontWeight: 600 }}
                        >
                          Ready to join
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ color: color.DARKERGRAY, marginTop: 8 }}
                        >
                          Click Join Meeting to start
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box className="zoom-video-main-container">
                      {/* Recording Indicator */}
                      {isRecording && (
                        <Box className="zoom-recording-indicator">
                          <FiberManualRecordIcon
                            style={{ fontSize: 14, color: "#ff1744" }}
                          />
                          <Typography
                            variant="caption"
                            style={{ fontWeight: 600 }}
                          >
                            REC
                          </Typography>
                        </Box>
                      )}

                      {remoteUsers.length > 0 ? (
                        // Show participant full screen with current user in top-right
                        <>
                          {/* Main participant video (full screen) */}
                          <Box className="zoom-main-participant">
                            <VideoParticipant
                              key={`main-video-${remoteUsers[0].userId}`}
                              userId={remoteUsers[0].userId}
                              displayName={remoteUsers[0].displayName}
                              audioMuted={remoteUsers[0].muted}
                              videoOn={remoteUsers[0].bVideoOn}
                              isMainView={true}
                            />
                          </Box>

                          {/* Current user overlay (top-right) */}
                          <Box className="zoom-self-view-overlay">
                            <div
                              ref={videoContainerRef}
                              className="zoom-self-video"
                            />
                            <Typography
                              variant="caption"
                              className="zoom-self-label"
                            >
                              {username} (You)
                            </Typography>
                            {localAudioMuted && (
                              <Box className="zoom-self-muted-indicator">
                                <MicOffIcon
                                  style={{ color: "white", fontSize: 16 }}
                                />
                              </Box>
                            )}
                          </Box>

                          {/* Additional participants (if more than one) */}
                          {remoteUsers.length > 1 && (
                            <Box className="zoom-additional-participants">
                              {remoteUsers.slice(1).map((user, index) => (
                                <Box
                                  key={`additional-${user.userId}`}
                                  className="zoom-additional-participant"
                                >
                                  <VideoParticipant
                                    userId={user.userId}
                                    displayName={user.displayName}
                                    audioMuted={user.muted}
                                    videoOn={user.bVideoOn}
                                    isSmallView={true}
                                  />
                                </Box>
                              ))}
                            </Box>
                          )}
                        </>
                      ) : (
                        // Show only current user when no participants
                        <Box className="zoom-solo-view">
                          <div
                            ref={videoContainerRef}
                            className="zoom-solo-video"
                          />
                          <Typography className="zoom-solo-label">
                            {username} (You)
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>

                {/* Chat Drawer - Slide in from right */}
                {/* <Drawer
                anchor="right"
                open={sidebar === "chat" && connected}
                onClose={() => setSidebar(null)}
                variant={isMobile ? "temporary" : "persistent"}
                classes={{
                  paper: "zoom-chat-drawer"
                }}
                PaperProps={{
                  style: { width: isMobile ? "100%" : "350px", maxWidth: "100%" }
                }}
              >
                <ChatWindow messages={messages} zoomClient={client} />
              </Drawer> */}
              </Box>

              {/* Controls */}
              <Box className="zoom-controls-modern">
                {!connected ? (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleJoin}
                    disabled={connecting || isInitializing}
                    className="zoom-join-btn"
                    style={{
                      minWidth: isMobile ? 150 : 180,
                      ...(connecting
                        ? { background: color.LIGHTGRAY, boxShadow: "none" }
                        : {})
                    }}
                  >
                    {isInitializing
                      ? "Initializing..."
                      : connecting
                      ? "Connecting..."
                      : "Join Meeting"}
                  </Button>
                ) : (
                  <Box className="zoom-control-buttons">
                    <ControlButton
                      title={localAudioMuted ? "Unmute" : "Mute"}
                      active={localAudioMuted}
                      onClick={handleToggleAudio}
                      icon={localAudioMuted ? <MicOffIcon /> : <MicIcon />}
                    />
                    <ControlButton
                      title={videoOn ? "Turn off camera" : "Turn on camera"}
                      active={!videoOn}
                      onClick={handleToggleVideo}
                      icon={videoOn ? <VideocamIcon /> : <VideocamOffIcon />}
                    />
                    <ControlButton
                      title={isSharing ? "Stop sharing" : "Share screen"}
                      active={isSharing}
                      onClick={isSharing ? stopScreenShare : startScreenShare}
                      icon={<ScreenShareIcon />}
                    />
                    {/* <ControlButton
                    title={sidebar === "chat" ? "Close chat" : "Open chat"}
                    active={sidebar === "chat"}
                    onClick={() => setSidebar(sidebar === "chat" ? null : "chat")}
                    icon={
                      <Badge
                        color="primary"
                        variant="dot"
                        invisible={messages.length === 0 || sidebar === "chat"}
                      >
                        <ChatIcon />
                      </Badge>
                    }
                  /> */}
                    {/* {isRecording ? (
                    <ControlButton
                      title="Stop Recording"
                      active={true}
                      onClick={handleStopRecording}
                      icon={<StopIcon />}
                    />
                  ) : (
                    <ControlButton
                      title="Start Recording"
                      onClick={handleStartRecording}
                      icon={<FiberManualRecordIcon />}
                    />
                  )} */}
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<CallEndIcon />}
                      onClick={handleLeave}
                      className="zoom-leave-btn"
                      size={isMobile ? "small" : "medium"}
                    >
                      {isMobile ? "" : "Leave"}
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Hidden elements */}
              <Box style={{ display: "none" }}>
                <video id="my-screen-share-content-video" />
                <canvas id="my-screen-share-content-canvas" />
                <video id="users-screen-share-content-video" />
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </>
  );
});
